/** Route slug ↔ `/legal/employment-labour-law`. */
export const EMPLOYMENT_LABOUR_LAW_SLUG = "employment-labour-law";

export const employmentLabourLawDefaultData = {
  heroImage:
    "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1500&q=80",
  galleryImages: [
    "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=900&q=80",
    "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=900&q=80",
    "https://images.unsplash.com/photo-1507679799987-c73779587ccf?auto=format&fit=crop&w=900&q=80",
  ],
  stats: [
    { value: "Act 651", label: "Labour Act" },
    { value: "24h", label: "Expert Response" },
    { value: "NLC", label: "Disputes" },
  ],
  funnelTagline: "Contracts → fair termination → NLC & courts → speak to a labour lawyer",
  focusAreas: [
    "Wrongful termination",
    "Employment contracts",
    "Workplace disputes",
    "Compensation claims",
  ],
  primaryCtaHeading: "Talk to a labour lawyer",
  introduction: {
    headline: "WHY EMPLOYMENT & LABOUR LAW MATTERS IN GHANA",
    lead: "Employment is one of the most important parts of life—but many Ghanaians do not fully understand their rights at work.",
    leadsToHeading: "This leads to:",
    leadsTo: ["Unfair dismissals", "Unpaid salaries", "Workplace conflicts", "Exploitation"],
    mainLawLead: "The main law governing employment in Ghana is:",
    actName: "Labour Act, 2003 (Act 651)",
    protectsLead: "This law protects both:",
    protects: ["Employees", "Employers"],
    closing: "Understanding it helps you avoid being taken advantage of.",
  },
  contracts: {
    title: "EMPLOYMENT CONTRACTS (YOUR FIRST PROTECTION)",
    whatHeading: "What is an Employment Contract?",
    whatLead: "A contract is a legal agreement between you and your employer that defines:",
    defines: ["Your role", "Salary", "Working conditions", "Rights and obligations"],
    typesHeading: "Types of Employment Contracts in Ghana",
    types: ["Permanent employment", "Temporary or fixed-term contracts", "Casual work", "Probationary contracts"],
    mustIncludeHeading: "What Your Contract Must Include",
    mustInclude: [
      "Job description",
      "Salary and benefits",
      "Working hours",
      "Leave entitlement",
      "Termination conditions",
    ],
    problemsHeading: "Common Problems",
    problems: [
      "Working without a written contract",
      "Signing contracts without understanding",
      "Missing key terms (salary, notice period)",
    ],
    tipHeading: "Tip — always:",
    tipItems: ["Request a written contract", "Read before signing", "Seek legal advice if unsure"],
  },
  wrongfulTermination: {
    title: "WRONGFUL TERMINATION (VERY COMMON ISSUE)",
    whatHeading: "What is Wrongful Termination?",
    whatBody: "Your employment is ended unfairly or illegally.",
    lawSays:
      "The Labour Act protects workers from unfair dismissal and requires employers to justify termination.",
    examplesHeading: "Examples of Unfair Termination",
    examples: [
      "Dismissal due to pregnancy",
      "Discrimination (religion, gender, etc.)",
      "Retaliation for reporting misconduct",
      "Termination without valid reason",
    ],
    groundsHeading: "Legal Grounds for Termination",
    grounds: ["Misconduct", "Incompetence", "Redundancy", "Breach of contract"],
    rightsHeading: "Your Rights If Dismissed",
    rights: [
      "Notice period or payment in lieu",
      "Severance (in some cases)",
      "Compensation",
      "Reinstatement",
    ],
    whereHelpLead: "National Labour Commission (NLC) handles disputes.",
    importantHeading: "Important",
    importantBody: "There is a difference between:",
    wrongfulLabel: "Wrongful termination (breach of contract)",
    unfairLabel: "Unfair termination (violates labour law)",
  },
  workplaceDisputes: {
    title: "WORKPLACE DISPUTES (COMMON REALITY)",
    typesHeading: "Types of Workplace Disputes",
    types: [
      "Salary disputes",
      "Harassment or discrimination",
      "Unfair workload",
      "Breach of contract",
      "Disciplinary issues",
    ],
    resolutionHeading: "How Disputes Are Resolved",
    resolutionSteps: ["Internal resolution (HR)", "Mediation", "National Labour Commission", "Court (if necessary)"],
    mistakesHeading: "Common Mistakes",
    mistakes: ["Ignoring issues", "Not documenting complaints", "Acting emotionally instead of legally"],
    bestHeading: "Best Practice",
    bestItems: ["Keep records (emails, messages, contracts)", "Report issues formally", "Seek legal advice early"],
  },
  compensation: {
    title: "COMPENSATION & EMPLOYEE BENEFITS",
    salaryTitle: "Salary",
    salaryBody: "Must be paid regularly and fully.",
    leaveTitle: "Leave Entitlement",
    leaveItems: ["Annual leave", "Sick leave", "Maternity leave"],
    overtimeTitle: "Overtime Pay",
    overtimeBody: "If applicable.",
    severanceTitle: "Severance Pay",
    severanceBody: "In cases like redundancy.",
    issuesHeading: "Common Issues",
    issues: ["Unpaid salaries", "No leave granted", "No severance after redundancy"],
  },
  terminationProcess: {
    title: "TERMINATION PROCESS (HOW IT SHOULD WORK)",
    mustHeading: "Proper Termination Must Include:",
    mustInclude: ["Valid reason", "Proper notice", "Fair procedure"],
    typesHeading: "Types of Termination",
    types: ["Resignation (employee)", "Dismissal (employer)", "Redundancy", "Retirement"],
    insightHeading: "Important Insight",
    insightBody:
      "Employers must follow both legal rules and fair procedures.",
  },
  employeeRights: {
    title: "EMPLOYEE RIGHTS IN GHANA",
    coreHeading: "Your Core Rights",
    core: [
      "Right to fair treatment",
      "Right to safe working conditions",
      "Right to join a trade union",
      "Right to equal pay",
      "Right to legal protection",
    ],
    principleHeading: "Key Principle",
    principleLead: "You cannot be treated unfairly simply because:",
    principleBecause: [
      "You reported an issue",
      "You joined a union",
      "You exercised your legal rights",
    ],
  },
  employerResponsibilities: {
    title: "EMPLOYER RESPONSIBILITIES",
    mustHeading: "Employers must:",
    mustDo: [
      "Provide safe working conditions",
      "Pay wages on time",
      "Respect contracts",
      "Follow labour laws",
    ],
    failHeading: "If Employers Fail",
    failItems: ["Report to NLC", "Take legal action"],
  },
  commonProblemsGhana: {
    title: "COMMON PROBLEMS GHANAIANS FACE",
    intro: "Top issues",
    items: [
      "Being dismissed without notice",
      "Working without contracts",
      "Salary delays",
      "Workplace harassment",
      "No compensation after termination",
    ],
  },
  practicalTips: {
    title: "PRACTICAL TIPS FOR GHANAIANS",
    beforeJob: {
      heading: "Before Accepting a Job",
      items: ["Request written contract", "Confirm salary and benefits", "Understand termination terms"],
    },
    during: {
      heading: "During Employment",
      items: ["Keep records", "Know your rights", "Communicate professionally"],
    },
    dispute: {
      heading: "If You Have a Dispute",
      items: ["Document everything", "Report internally", "Seek legal advice"],
    },
    dismissed: {
      heading: "If You Are Dismissed",
      items: ["Ask for reason", "Check your contract", "Contact labour authorities"],
    },
  },
  redFlags: {
    title: "RED FLAGS TO WATCH OUT FOR",
    items: [
      "No written contract",
      "Salary delays",
      "Verbal-only agreements",
      "Sudden termination without explanation",
      "No HR structure",
      "Pressure to sign unclear documents",
    ],
  },
  editorialPlaybook: {
    title: "HIGH-CONVERTING INFOBOXX CONTENT IDEAS",
    articlesHeading: "Articles",
    articles: [
      "“Your Rights as an Employee in Ghana”",
      "“What to Do If You Are Wrongfully Dismissed”",
      "“Employment Contracts Explained Simply”",
    ],
    videosHeading: "Videos",
    videos: ["Labour law basics", "Wrongful termination explained", "Workplace dispute solutions"],
    leadCtasHeading: "CTAs",
    leadCtas: [
      "Talk to a labour lawyer",
      "Report unfair treatment",
      "Review your contract",
      "Get legal advice",
    ],
  },
  funnelApplication: {
    title: "INFOBOXX FUNNEL APPLICATION",
    hook: { label: "Hook", text: "“Can my employer sack me without notice?”" },
    education: { label: "Education", text: "Explain termination laws + rights" },
    authority: { label: "Authority", text: "Labour Act + procedures" },
    conversion: { label: "Conversion", text: "“Speak to a labour lawyer now”" },
  },
  scenario: {
    title: "REAL-LIFE SCENARIO",
    eyebrow: "Engagement example",
    exampleLead: "You are dismissed suddenly:",
    bullets: [
      "You must be given notice or payment",
      "Employer must have valid reason",
      "You can challenge it",
    ],
    optionsHeading: "Your options:",
    options: ["File complaint with NLC", "Seek compensation", "Take legal action"],
  },
  finalTakeaway: {
    title: "FINAL TAKEAWAY",
    bullets: [
      "A job is not just work—it is a legal relationship",
      "You have rights—even if you are not aware of them",
      "Employers must follow the law",
    ],
  },
  bottomLine: {
    title: "BOTTOM LINE",
    lead: "The difference between:",
    smooth: "Being treated fairly",
    and: "and",
    stressful: "Being exploited at work",
    closing: "…is knowing your rights and taking action early.",
  },
  faqs: [
    {
      q: "What is the main employment law in Ghana?",
      a: "The Labour Act, 2003 (Act 651) is the cornerstone statute for most employment relationships, dealing with contracts, termination, dispute resolution, and related matters.",
    },
    {
      q: "Do I need a written employment contract?",
      a: "A written contract is your clearest protection — it should cover role, pay, hours, leave, and termination terms. Oral arrangements are harder to prove if a dispute arises.",
    },
    {
      q: "What is unfair vs wrongful termination?",
      a: "Wrongful termination often refers to breach of contractual terms or procedure; unfair termination concerns failure to comply with labour law fairness standards including valid reason and fair hearing. Practical advice differs — document everything and involve counsel or NLC early.",
    },
    {
      q: "Where can I report unfair dismissal?",
      a: "The National Labour Commission (NLC) is a central forum for many labour grievances after internal steps fail. Court action may remain available depending on remedies sought.",
    },
    {
      q: "Can I recover unpaid salary?",
      a: "Unpaid wages and denied statutory benefits can often be chased through grievance channels, mediation, NLC proceedings, or court where appropriate — evidence such as payslips, contracts, and bank records helps.",
    },
  ],
  articles: [
    {
      id: "ell-a-1",
      slug: "employment-labour-rights-employee-ghana",
      title: "Your Rights as an Employee in Ghana",
      excerpt:
        "Act 651 basics, contracts, dismissal fairness, unions, discrimination protection, and when to escalate to NLC or court.",
      type: "Guide",
      readTime: "9 min read",
      author: "InfoBoxx Editorial Desk",
      publishedAt: "Updated recently",
      updatedAt: "Updated recently",
      socialText: 'Read: "Your Rights as an Employee in Ghana"',
      coverImage:
        "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=800&q=80",
      tags: ["Act 651", "Rights", "Ghana"],
      pullQuote: "A job is a legal relationship — know your contract and your remedies.",
      takeaways: ["Read your contract end-to-end", "Document pay and conduct issues", "Use formal channels early"],
      video: {
        title: "Labour law basics",
        duration: "5:20",
        poster:
          "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=800&q=80",
      },
      body: [
        {
          heading: "Why Labour Law Matters",
          text: "Most workplace stress comes from unclear terms, delayed pay, or unfair exits — all areas Act 651 and your contract address.",
        },
        {
          heading: "Core Contract Clauses",
          bullets: ["Role", "Remuneration", "Hours", "Leave", "Termination"],
        },
        {
          heading: "Escalation Path",
          text: "Internal HR → mediation → NLC → court where merited. Records make each step stronger.",
        },
      ],
    },
    {
      id: "ell-a-2",
      slug: "employment-labour-wrongful-dismissal-ghana",
      title: "What to Do If You Are Wrongfully Dismissed",
      excerpt:
        "Valid reasons, fair procedure, notice and pay in lieu, severance, reinstatement claims, and filing with the NLC.",
      type: "Checklist",
      readTime: "8 min read",
      author: "Partner Knowledge Team",
      publishedAt: "This week",
      updatedAt: "This week",
      socialText: 'Read: "What to Do If You Are Wrongfully Dismissed"',
      coverImage:
        "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=800&q=80",
      tags: ["Dismissal", "NLC", "Remedies"],
      pullQuote: "Fairness has two parts: a lawful reason and a fair process.",
      takeaways: ["Request reasons in writing", "Compare to contract + Act 651", "Time limits matter — act fast"],
      video: {
        title: "Wrongful termination explained",
        duration: "4:45",
        poster:
          "https://images.unsplash.com/photo-1507679799987-c73779587ccf?auto=format&fit=crop&w=800&q=80",
      },
      body: [
        {
          heading: "Gather Evidence Immediately",
          bullets: ["Termination letter", "Payslips", "Emails", "Witnesses"],
        },
        {
          heading: "Unfair Examples",
          text: "",
          bullets: [
            "Pregnancy-related dismissal",
            "Retaliation for whistleblowing",
            "Termination without substantive reason",
          ],
        },
        {
          heading: "Next Steps",
          text: "Internal appeal if available, mediation, then NLC or court guided by counsel.",
        },
      ],
    },
    {
      id: "ell-a-3",
      slug: "employment-labour-contracts-explained-ghana",
      title: "Employment Contracts Explained Simply",
      excerpt:
        "Permanent vs fixed-term vs casual probation, mandatory terms, pitfalls of verbal deals, and review before signing.",
      type: "Expert Explainer",
      readTime: "7 min read",
      author: "Consumer Guidance Unit",
      publishedAt: "Reviewed this month",
      updatedAt: "Reviewed this month",
      socialText: 'Read: "Employment Contracts Explained Simply"',
      coverImage:
        "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=800&q=80",
      tags: ["Contracts", "Notice", "Leave"],
      pullQuote: "Never sign clauses you cannot live with — negotiation is cheaper than litigation.",
      takeaways: ["Define notice periods", "Clarify bonuses and deductions", "Get a second opinion"],
      video: {
        title: "Workplace dispute solutions",
        duration: "6:10",
        poster:
          "https://images.unsplash.com/photo-1521791055364-18956bb5568e?auto=format&fit=crop&w=800&q=80",
      },
      body: [
        {
          heading: "Types of Engagement",
          bullets: ["Permanent", "Temporary / fixed-term", "Casual", "Probation"],
        },
        {
          heading: "Must-have Terms",
          text: "",
          bullets: ["Job scope", "Pay + benefits", "Hours", "Leave", "Termination"],
        },
        {
          heading: "Common Pitfalls",
          bullets: ["No written agreement", "Vague KPIs", "One-sided restraint clauses"],
        },
      ],
    },
  ],
  videos: [
    {
      id: "ell-v-1",
      title: "Labour law basics",
      duration: "5:20",
      poster:
        "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=800&q=80",
      description: "Act 651 roadmap for everyday employees and SMEs.",
    },
    {
      id: "ell-v-2",
      title: "Wrongful termination explained",
      duration: "4:45",
      poster:
        "https://images.unsplash.com/photo-1507679799987-c73779587ccf?auto=format&fit=crop&w=800&q=80",
      description: "Reason, procedure, and where the NLC fits.",
    },
    {
      id: "ell-v-3",
      title: "Workplace dispute solutions",
      duration: "6:10",
      poster:
        "https://images.unsplash.com/photo-1521791055364-18956bb5568e?auto=format&fit=crop&w=800&q=80",
      description: "HR steps, documentation, mediation, and formal complaints.",
    },
  ],
  podcasts: [
    {
      id: "ell-p-1",
      episodeLabel: "Episode 1",
      duration: "28 min",
      title: "Act 651 in Plain English for Staff & HR",
      description: "Contracts, discipline, and the difference between policy and law.",
      host: "Afrimore Legal Desk",
      publishedAt: "May 2026",
      coverImage:
        "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=800&q=80",
    },
    {
      id: "ell-p-2",
      episodeLabel: "Episode 2",
      duration: "32 min",
      title: "NLC Filings: What Actually Happens",
      description: "Timelines, bundles of evidence, and realistic outcomes.",
      host: "Consumer Guidance Unit · Infoboxx",
      publishedAt: "April 2026",
      coverImage:
        "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=800&q=80",
    },
    {
      id: "ell-p-3",
      episodeLabel: "Mini",
      duration: "17 min",
      title: "Salary Delays & Redundancy Payouts",
      description: "Negotiation levers before you walk away from severance.",
      host: "Afrimore Legal Desk",
      publishedAt: "March 2026",
      coverImage:
        "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=800&q=80",
    },
  ],
};
